"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { ApplyNowEmailCapture } from "@/components/forms/apply-now-email-capture";
import {
  EMAIL_CAPTURE_PILL_FORM_CLASS,
  EMAIL_CAPTURE_PILL_INPUT_CLASS,
} from "@/components/forms/email-capture-pill-styles";
import { NewsletterEmailCapture } from "@/components/forms/newsletter-email-capture";
import { ArrowCtaButton } from "@/components/ui/arrow-cta-button";
import { useApplyNowSubmit } from "@/hooks/use-apply-now-submit";
import { useElementActivity } from "@/hooks/use-element-activity";
import { useNewsletterSubscribe } from "@/hooks/use-newsletter-subscribe";
import { cn } from "@/lib/utils";

import styles from "./growth-stories-arc.module.css";

const ARC_IMAGE_COUNT = 11;

/** Sanity CDN URLs for growth-arc portrait tiles (WEB-723 batch 2). */
const ARC_IMAGE_URLS = [
  "https://cdn.sanity.io/images/1aendizt/redesign/5e98f827cc1089906510d2130a6e9b87dd356e25-1200x896.png",
  "https://cdn.sanity.io/images/1aendizt/redesign/92580d7a2c5da5fb43d913b46775b541fcb2fe0a-960x1200.png",
  "https://cdn.sanity.io/images/1aendizt/redesign/72ae87877c3adb7f356e0002c0890fd0d6fa47e5-900x1200.png",
  "https://cdn.sanity.io/images/1aendizt/redesign/641b8d799035d5495ca2e6627bd14c9836b63ee2-960x1200.png",
  "https://cdn.sanity.io/images/1aendizt/redesign/27783e0ee983083cf72f0b6275b8d8b77ed42ec1-1200x673.png",
  "https://cdn.sanity.io/images/1aendizt/redesign/a0a6b566e91ea75792fbbd87df2dfd7c087c7873-904x1200.png",
  "https://cdn.sanity.io/images/1aendizt/redesign/55fb190edcc67665a737a4519e53e4abb239a21f-1200x673.png",
  "https://cdn.sanity.io/images/1aendizt/redesign/ed546be4c35ab987ba1e68ce980fe57003b7312a-888x836.png",
  "https://cdn.sanity.io/images/1aendizt/redesign/69f6a0be4fdbc795e62185b467b26ff2ececfc7c-904x1200.png",
  "https://cdn.sanity.io/images/1aendizt/redesign/27bce2a3e770e7e2d5020fb58fe499288d5b8e61-686x1200.png",
  "https://cdn.sanity.io/images/1aendizt/redesign/040cb32c0c2d1d6a2228bac0cde34ebef902f66e-1200x904.png",
] as const;

/** ~18° between Figma's 11 semicircle cards — repeat around 360° for a continuous orbit. */
const FIGMA_ANGULAR_STEP_DEG = 18;
const ORBIT_CARD_COUNT = Math.round(360 / FIGMA_ANGULAR_STEP_DEG);

type ArcTile = {
  x: number;
  y: number;
  rot: number;
  flip: boolean;
  imageIndex: number;
};

/** Figma inward-facing rot/flip: right arc uses rotate(θ+90), left arc uses rotate(270+θ) + scaleY(-1). */
function figmaInwardOrientation(
  px: number,
  py: number,
  cx: number,
  cy: number
): Pick<ArcTile, "rot" | "flip"> {
  const angleDeg = (Math.atan2(py - cy, px - cx) * 180) / Math.PI;
  const flip = px < cx;

  return flip
    ? { rot: 270 + angleDeg, flip: true }
    : { rot: angleDeg + 90, flip: false };
}

function generateContinuousOrbit({
  count,
  frameWidth,
  frameHeight,
  centerX,
  centerY,
  radius,
  startAngleDeg = -90,
}: {
  count: number;
  frameWidth: number;
  frameHeight: number;
  centerX: number;
  centerY: number;
  radius: number;
  startAngleDeg?: number;
}): ArcTile[] {
  return Array.from({ length: count }, (_, index) => {
    const angleDeg = startAngleDeg + (360 / count) * index;
    const angleRad = (angleDeg * Math.PI) / 180;
    const px = centerX + radius * Math.cos(angleRad);
    const py = centerY + radius * Math.sin(angleRad);
    const { rot, flip } = figmaInwardOrientation(px, py, centerX, centerY);

    return {
      x: (px / frameWidth) * 100,
      y: (py / frameHeight) * 100,
      rot,
      flip,
      // Stagger image assignment so neighbours rarely repeat the same photo.
      imageIndex: (index * 2) % ARC_IMAGE_COUNT,
    };
  });
}

const DESKTOP_ORBIT = generateContinuousOrbit({
  count: ORBIT_CARD_COUNT,
  frameWidth: 1440,
  frameHeight: 768,
  centerX: 718,
  centerY: 629.5,
  radius: 487,
});

/** Figma tablet frame 768×648 — orbit scaled from desktop proportions. */
const TABLET_ORBIT = generateContinuousOrbit({
  count: ORBIT_CARD_COUNT,
  frameWidth: 768,
  frameHeight: 648,
  centerX: (718 * 768) / 1440,
  centerY: (629.5 * 648) / 768,
  radius: (487 * 648) / 768,
});

const MOBILE_ORBIT = generateContinuousOrbit({
  count: ORBIT_CARD_COUNT,
  frameWidth: 375,
  frameHeight: 439,
  centerX: 187.5,
  centerY: 338,
  radius: 240,
});

/** Figma mobile frame 375×572 — content/overlay positions (px → % helpers). */
const MOBILE_FRAME = { width: 375, height: 572 } as const;
const MOBILE_OVERLAY_TOP = `${(142 / MOBILE_FRAME.height) * 100}%`;
/** Figma mobile overlay: 133px fade band (142→275), not full-bleed to section bottom. */
const MOBILE_OVERLAY_HEIGHT = `${(133 / MOBILE_FRAME.height) * 100}%`;

/** Figma tablet frame 768×648. */
const TABLET_FRAME = { width: 768, height: 648 } as const;
const TABLET_CONTENT_TOP = `${(276 / TABLET_FRAME.height) * 100}%`;
const TABLET_OVERLAY_TOP = `${(380 / TABLET_FRAME.height) * 100}%`;

/** Figma desktop frame 1440×768. */
const DESKTOP_FRAME = { width: 1440, height: 768 } as const;
const DESKTOP_CONTENT_TOP = `${(353.66 / DESKTOP_FRAME.height) * 100}%`;
const DESKTOP_CONTENT_WIDTH = `${(450.592 / DESKTOP_FRAME.width) * 100}%`;
const DESKTOP_OVERLAY_TOP = `${(384 / DESKTOP_FRAME.height) * 100}%`;

/** Orbit pivot from Figma desktop frame 1440×768 (718px, 629.5px). */
const DESKTOP_ORBIT_ORIGIN = "49.86% 81.97%";

/** Orbit pivot for Figma tablet frame 768×648 — same proportional point. */
const TABLET_ORBIT_ORIGIN = "49.86% 81.97%";

/** Orbit pivot for mobile frame 375×439 — bottom-arc centre (~50%, 77%). */
const MOBILE_ORBIT_ORIGIN = "50% 77%";

interface GrowthStoriesArcProps {
  title: string;
  titleDesktop: string;
  /** Optional supporting line under the title (omitted in the 2026 design). */
  subtitle?: string;
  /**
   * Wrap the mobile title naturally at 32px (Figma 12364:98602) instead of
   * forcing a break after the second word.
   */
  wrapMobileTitle?: boolean;
  emailPlaceholder: string;
  /** Mobile placeholder without trailing punctuation (Figma: "Your work email"). */
  emailPlaceholderShort: string;
  applyLabel: string;
  sendingLabel?: string;
  /**
   * Subscribe mode — submits to the HubSpot Insights newsletter form and stays
   * on the page. Default (false) keeps the apply-now capture that redirects to
   * signup.
   */
  subscribe?: boolean;
  /** Confirmation copy shown in place of the form in subscribe mode. */
  subscribeSuccessMessage?: string;
  className?: string;
}

export function GrowthStoriesArc({
  title,
  titleDesktop,
  subtitle,
  wrapMobileTitle = false,
  emailPlaceholder,
  emailPlaceholderShort,
  applyLabel,
  sendingLabel = "Sending…",
  subscribe = false,
  subscribeSuccessMessage = "Thanks, you're subscribed.",
  className,
}: GrowthStoriesArcProps) {
  const { ref: sectionRef, isActive: isSectionActive } =
    useElementActivity<HTMLElement>();
  // Mobile title breaks after the second word to match the Figma
  // ("Growth Stories" / "Built for What's Next").
  const titleWords = title.split(" ");
  const mobileTitleLines =
    titleWords.length > 2
      ? [titleWords.slice(0, 2).join(" "), titleWords.slice(2).join(" ")]
      : [title];

  return (
    <section
      ref={sectionRef}
      data-testid="our-customers-growth-stories-section"
      className={cn("bg-[#f9f9f8]", className)}
    >
      {/* Tablet: Figma 768×648 — orbit cards fill the lower area (no empty gap). */}
      <div className="relative hidden aspect-[768/648] w-full overflow-hidden md:block lg:hidden">
        <div
          aria-hidden
          className={styles["orbitRing"]}
          data-paused={!isSectionActive || undefined}
          style={{ transformOrigin: TABLET_ORBIT_ORIGIN }}
        >
          {TABLET_ORBIT.map((tile, index) => (
            <div
              key={index}
              className="rounded-ds-xxl absolute aspect-square overflow-hidden border-4 border-white bg-white shadow-[0_4px_24px_0_rgba(0,0,0,0.1)]"
              style={{
                left: `${tile.x}%`,
                top: `${tile.y}%`,
                width: "12.0575%",
                transform: `translate(-50%,-50%) rotate(${tile.rot}deg) scaleY(${tile.flip ? -1 : 1})`,
              }}
            >
              <Image
                src={ARC_IMAGE_URLS[tile.imageIndex]!}
                alt=""
                fill
                loading="lazy"
                quality={90}
                sizes="15vw"
                className="object-cover"
              />
            </div>
          ))}
        </div>

        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0"
          style={{
            top: TABLET_OVERLAY_TOP,
            background:
              "linear-gradient(to bottom, rgba(249,249,248,0) 0%, #f9f9f8 80.912%)",
          }}
        />

        <div
          className="absolute left-1/2 flex w-[55.2083%] max-w-[424px] -translate-x-1/2 flex-col items-center gap-6"
          style={{ top: TABLET_CONTENT_TOP }}
        >
          <h2 className="text-center text-[44px] leading-[1.1] tracking-[-1.76px] text-[#0c0d0d]">
            {titleDesktop}
          </h2>
          {subtitle ? (
            <p className="text-center text-[14px] leading-[1.4] tracking-[-0.02em] text-[#7b8084]">
              {subtitle}
            </p>
          ) : null}
          <GrowthStoriesApplyForm
            emailPlaceholder={emailPlaceholder}
            applyLabel={applyLabel}
            sendingLabel={sendingLabel}
            subscribe={subscribe}
            subscribeSuccessMessage={subscribeSuccessMessage}
            variant="tablet"
          />
        </div>
      </div>

      {/* Desktop: Figma 1440×768 */}
      <div className="relative hidden aspect-[1440/768] w-full overflow-hidden lg:block">
        <div
          aria-hidden
          className={styles["orbitRing"]}
          data-paused={!isSectionActive || undefined}
          style={{ transformOrigin: DESKTOP_ORBIT_ORIGIN }}
        >
          {DESKTOP_ORBIT.map((tile, index) => (
            <div
              key={index}
              className="rounded-ds-xxl absolute aspect-square overflow-hidden border-[0.28vw] border-white bg-white shadow-[0_0.28vw_1.67vw_0_rgba(0,0,0,0.1)]"
              style={{
                left: `${tile.x}%`,
                top: `${tile.y}%`,
                width: "7.569vw",
                transform: `translate(-50%,-50%) rotate(${tile.rot}deg) scaleY(${tile.flip ? -1 : 1})`,
              }}
            >
              <Image
                src={ARC_IMAGE_URLS[tile.imageIndex]!}
                alt=""
                fill
                loading="lazy"
                quality={90}
                sizes="10vw"
                className="object-cover"
              />
            </div>
          ))}
        </div>

        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0"
          style={{
            top: DESKTOP_OVERLAY_TOP,
            background:
              "linear-gradient(to bottom, rgba(249,249,248,0) 0%, #f9f9f8 80.912%)",
          }}
        />

        <div
          className="absolute left-1/2 flex -translate-x-1/2 flex-col items-center"
          style={{
            top: DESKTOP_CONTENT_TOP,
            width: DESKTOP_CONTENT_WIDTH,
            gap: "1.667vw",
          }}
        >
          <h2
            className="w-full text-center leading-[1.1] tracking-[-0.04em]"
            style={{ color: "#0c0d0d", fontSize: "3.056vw" }}
          >
            {titleDesktop}
          </h2>
          {subtitle ? (
            <p
              className="w-full text-center leading-[1.4] tracking-[-0.02em]"
              style={{ color: "#7b8084", fontSize: "0.972vw" }}
            >
              {subtitle}
            </p>
          ) : null}
          <GrowthStoriesApplyForm
            emailPlaceholder={emailPlaceholder}
            applyLabel={applyLabel}
            sendingLabel={sendingLabel}
            subscribe={subscribe}
            subscribeSuccessMessage={subscribeSuccessMessage}
          />
        </div>
      </div>

      {/* Mobile: outer 375×572 section height; inner 375×439 arc canvas. */}
      <div className="relative aspect-[375/572] w-full overflow-hidden md:hidden">
        <div className="absolute inset-x-0 top-0 aspect-[375/439] w-full overflow-hidden">
          <div
            aria-hidden
            className={styles["orbitRing"]}
            data-paused={!isSectionActive || undefined}
            style={{ transformOrigin: MOBILE_ORBIT_ORIGIN }}
          >
            {MOBILE_ORBIT.map((tile, index) => (
              <div
                key={index}
                className="rounded-ds-xxl absolute aspect-square w-[13.9%] overflow-hidden border-4 border-white bg-white shadow-[0_4px_24px_0_rgba(0,0,0,0.1)]"
                style={{
                  left: `${tile.x}%`,
                  top: `${tile.y}%`,
                  transform: `translate(-50%,-50%) rotate(${tile.rot}deg) scaleY(${tile.flip ? -1 : 1})`,
                }}
              >
                <Image
                  src={ARC_IMAGE_URLS[tile.imageIndex]!}
                  alt=""
                  fill
                  loading="lazy"
                  quality={90}
                  sizes="18vw"
                  className="object-cover"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Figma overlay: top 142px, height 133px on 572px frame */}
        <div
          aria-hidden
          className="absolute inset-x-0"
          style={{
            top: MOBILE_OVERLAY_TOP,
            height: MOBILE_OVERLAY_HEIGHT,
            background:
              "linear-gradient(to bottom, rgba(249,249,248,0) 0%, #f9f9f8 80.912%)",
          }}
        />

        {/* Figma content: top 276px @ sm+, tighter below sm — width 327px, gap 40px */}
        <div
          className={cn(
            wrapMobileTitle
              ? styles["mobileContentTight"]
              : styles["mobileContent"],
            "absolute left-1/2 flex w-[87.2%] max-w-[327px] -translate-x-1/2 flex-col items-center"
          )}
          style={{ gap: "40px" }}
        >
          <h2
            className={cn(
              "w-full text-center leading-[1.2] text-[#0c0d0d]",
              wrapMobileTitle
                ? "text-[32px] tracking-[-1.28px] text-balance"
                : "text-[30px] tracking-[-1.2px]"
            )}
          >
            {wrapMobileTitle
              ? title
              : mobileTitleLines.map((line, i) => (
                  <span key={i} className="block">
                    {line}
                  </span>
                ))}
          </h2>
          {subtitle ? (
            <p
              className={cn(
                "w-full text-center leading-[1.4] tracking-[-0.02em] text-[#7b8084]",
                wrapMobileTitle ? "text-[16px]" : "text-[14px]"
              )}
            >
              {subtitle}
            </p>
          ) : null}
          <GrowthStoriesApplyForm
            emailPlaceholder={emailPlaceholderShort}
            applyLabel={applyLabel}
            sendingLabel={sendingLabel}
            subscribe={subscribe}
            subscribeSuccessMessage={subscribeSuccessMessage}
            variant="mobile"
          />
        </div>
      </div>
    </section>
  );
}

function GrowthStoriesApplyForm({
  emailPlaceholder,
  applyLabel,
  sendingLabel,
  subscribe = false,
  subscribeSuccessMessage,
  variant = "desktop",
}: {
  emailPlaceholder: string;
  applyLabel: string;
  sendingLabel: string;
  subscribe?: boolean;
  subscribeSuccessMessage?: string;
  variant?: "desktop" | "tablet" | "mobile";
}) {
  if (variant === "desktop") {
    return (
      <GrowthStoriesDesktopApplyForm
        emailPlaceholder={emailPlaceholder}
        applyLabel={applyLabel}
        sendingLabel={sendingLabel}
        subscribe={subscribe}
        subscribeSuccessMessage={subscribeSuccessMessage}
      />
    );
  }

  if (subscribe) {
    return (
      <NewsletterEmailCapture
        id={`growth-stories-email-${variant}`}
        emailPlaceholder={emailPlaceholder}
        subscribeLabel={applyLabel}
        successMessage={subscribeSuccessMessage ?? ""}
        buttonLayout="hero-form"
        formClassName={cn(
          EMAIL_CAPTURE_PILL_FORM_CLASS,
          "rounded-[40px] border border-[#b0b3b5] bg-white"
        )}
        inputClassName={cn(
          EMAIL_CAPTURE_PILL_INPUT_CLASS,
          "text-left text-[#b0b3b5] placeholder:text-left"
        )}
      />
    );
  }

  return (
    <ApplyNowEmailCapture
      id={`growth-stories-email-${variant}`}
      emailPlaceholder={emailPlaceholder}
      applyLabel={applyLabel}
      sendingLabel={sendingLabel}
      buttonLayout="hero-form"
      ctaIntent="apply"
      ctaLocation="body"
      formClassName={cn(
        EMAIL_CAPTURE_PILL_FORM_CLASS,
        "rounded-[40px] border border-[#b0b3b5] bg-white"
      )}
      inputClassName={cn(
        EMAIL_CAPTURE_PILL_INPUT_CLASS,
        "text-left text-[#b0b3b5] placeholder:text-left"
      )}
      className="w-full"
    />
  );
}

/** Desktop vw-scaled form keeps ApplyNowEmailCapture layout with responsive sizing. */
function GrowthStoriesDesktopApplyForm({
  emailPlaceholder,
  applyLabel,
  sendingLabel,
  subscribe = false,
  subscribeSuccessMessage,
}: {
  emailPlaceholder: string;
  applyLabel: string;
  sendingLabel: string;
  subscribe?: boolean;
  subscribeSuccessMessage?: string;
}) {
  const applyNow = useApplyNowSubmit();
  const newsletter = useNewsletterSubscribe();
  const { submit, isSubmitting } = subscribe ? newsletter : applyNow;
  const t = useTranslations("Form");
  const schema = useMemo(
    () =>
      z.object({
        email: z
          .string()
          .trim()
          .min(1, { message: t("required") })
          .email({ message: t("invalidEmail") }),
      }),
    [t]
  );
  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<{ email: string }>({
    resolver: zodResolver(schema),
    defaultValues: { email: "" },
  });
  const emailId = "growth-stories-email-desktop";
  const errorId = `${emailId}-error`;
  const onSubmit = handleSubmit(async ({ email }) => {
    if (isSubmitting) return;
    await submit(email);
  });

  if (subscribe && newsletter.isSubscribed) {
    return (
      <p
        role="status"
        className="text-center font-medium text-[#0c0d0d]"
        style={{ fontSize: "0.972vw" }}
      >
        {subscribeSuccessMessage}
      </p>
    );
  }

  return (
    <form
      onSubmit={(event) => void onSubmit(event)}
      noValidate
      className="relative flex w-full items-center justify-between rounded-[2.778vw] border border-[#b0b3b5] bg-white"
      style={{
        paddingLeft: "1.111vw",
        paddingRight: "0.278vw",
        paddingTop: "0.278vw",
        paddingBottom: "0.278vw",
      }}
    >
      <input
        id={emailId}
        type="email"
        required
        autoComplete="email"
        placeholder={emailPlaceholder}
        aria-label={emailPlaceholder}
        aria-describedby={errors.email ? errorId : undefined}
        aria-invalid={Boolean(errors.email)}
        disabled={isSubmitting}
        className="min-w-0 flex-1 bg-transparent text-left leading-[1.4] font-medium text-[#b0b3b5] outline-none placeholder:text-left"
        style={{ fontSize: "0.972vw" }}
        {...register("email")}
      />
      <ArrowCtaButton
        type="submit"
        labelSize="inherit"
        disabled={isSubmitting}
        data-ph-capture-attribute-cta_label={applyLabel}
        data-ph-capture-attribute-cta_intent={subscribe ? undefined : "apply"}
        data-ph-capture-attribute-cta_location={subscribe ? undefined : "body"}
        className="shrink-0 text-[0.972vw]"
        style={{
          gap: "0.833vw",
          paddingLeft: "1.111vw",
          paddingRight: "0.486vw",
          paddingTop: "0.417vw",
          paddingBottom: "0.417vw",
        }}
        arrowClassName="size-[1.944vw]"
        arrowIconClassName="size-[1.111vw]"
      >
        {isSubmitting ? sendingLabel : applyLabel}
      </ArrowCtaButton>
      {errors.email ? (
        <p
          id={errorId}
          role="alert"
          className="text-text-critical absolute top-full left-[1.111vw] z-10 mt-[0.278vw] leading-[1.4]"
          style={{ fontSize: "0.833vw" }}
        >
          {errors.email.message}
        </p>
      ) : null}
    </form>
  );
}
